import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
    Map, Brain, FolderKanban, GraduationCap, Activity,
    LogOut, Globe, Snowflake, Layers, Sun, Moon, Rocket, Lock, Clock, Zap, Microscope,
} from "lucide-react";
import GroundSciences from "@/components/tabs/GroundSciences";
import GisMap from "@/components/tabs/GisMap";
import AiClassifier from "@/components/tabs/AiClassifier";
import ProjectWorkspace from "@/components/tabs/ProjectWorkspace";
import LearningPath from "@/components/tabs/LearningPath";
import TelemetryHub from "@/components/tabs/TelemetryHub";
import Plans from "@/components/tabs/Plans";
import { BrandLogo } from "@/components/BrandLogo";

const TABS = [
    { id: "gis", label: "Orbital GIS", icon: Map, testid: "nav-gis-map-tab" },
    { id: "ground", label: "Ground Sciences", icon: Microscope, testid: "nav-ground-sciences-tab" },
    { id: "classifier", label: "AI Classifier", icon: Brain, testid: "nav-ai-classifier-tab" },
    { id: "workspace", label: "Project Workspace", icon: FolderKanban, testid: "nav-workspace-tab" },
    { id: "learning", label: "Guided Learning", icon: GraduationCap, testid: "nav-learning-path-tab" },
    { id: "telemetry", label: "Telemetry Hub", icon: Activity, testid: "nav-telemetry-tab" },
    { id: "plans", label: "Plans", icon: Rocket, testid: "nav-plans-tab" },
];

function LockedModule({ title, onUpgrade }) {
    return (
        <div className="flex flex-col items-center justify-center text-center rounded-xl border border-ares-border bg-ares-dark/90 py-20 px-6" data-testid="locked-module">
            <div className="w-14 h-14 rounded-full bg-ares-orange/15 border border-ares-orange/40 flex items-center justify-center mb-5 glow-orange">
                <Lock className="w-6 h-6 text-ares-orange" />
            </div>
            <h3 className="font-heading text-2xl font-semibold">{title} is a Pro module</h3>
            <p className="text-ares-muted mt-2 max-w-md text-sm">Start your 14-day free trial to unlock the AI classifier, subsurface telemetry and every other mission module.</p>
            <Button onClick={onUpgrade} className="mt-6 h-11 bg-ares-orange hover:bg-[#e55a2b] text-white px-8" data-testid="locked-upgrade-btn">
                <Rocket className="w-4 h-4 mr-2" /> Unlock with ARES Pro
            </Button>
        </div>
    );
}

function MetricCard({ icon: Icon, label, value, accent, testid }) {
    return (
        <div className="relative rounded-xl border border-ares-border bg-ares-dark/90 p-4 overflow-hidden" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted">{label}</p>
                    <p className="font-heading text-2xl font-bold mt-1 text-ares-bright">{value}</p>
                </div>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
                    <Icon className="w-4.5 h-4.5" />
                </div>
            </div>
        </div>
    );
}

export default function Workspace() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const [active, setActive] = useState("gis");
    const [stats, setStats] = useState(null);
    const [sol, setSol] = useState(0);
    const [sub, setSub] = useState(null);

    const loadStats = async () => {
        try {
            const { data } = await api.get("/stats");
            setStats(data);
        } catch (e) { /* ignore */ }
    };

    const loadSub = async () => {
        try {
            const { data } = await api.get("/subscription");
            setSub(data);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => {
        loadStats();
        loadSub();
        const base = Math.floor((Date.now() / 1000 - 946684800) / 88775);
        setSol(base % 100000);
        const t = setInterval(() => setSol((s) => s), 60000);
        return () => clearInterval(t);
    }, []);

    const isActive = !!sub?.active;
    const trialDaysLeft = sub?.status === "trialing" && sub?.trial_end
        ? Math.max(0, Math.ceil((new Date(sub.trial_end) - new Date()) / 86400000))
        : null;
    const PREMIUM = { classifier: true, telemetry: true };
    const isLocked = (tabId) => PREMIUM[tabId] && !isActive;

    return (
        <div className="min-h-screen bg-ares-void text-ares-bright">
            {/* Header */}
            <header className="sticky top-0 z-50 glass">
                <div className="max-w-[1500px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5 shrink-0" data-testid="workspace-brand">
                        <BrandLogo />
                        <div className="leading-tight hidden sm:block">
                            <p className="font-heading font-bold tracking-tight text-sm">GeoMorphology</p>
                            <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted">Mission Console</p>
                        </div>
                    </div>

                    <div className="hidden lg:flex items-center gap-4 font-mono text-[11px] text-ares-muted">
                        <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-ares-orange" /> SOL <span className="text-ares-bright">{sol.toLocaleString()}</span></span>
                        <span className="flex items-center gap-1.5 text-ares-cyan"><Snowflake className="w-3.5 h-3.5" /> ICE-SCAN ACTIVE</span>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right hidden sm:block">
                            <p className="text-sm font-medium leading-tight" data-testid="user-name">{user?.name}</p>
                            <p className="font-mono text-[10px] uppercase tracking-wider text-ares-muted">{user?.role}</p>
                        </div>
                        <div className="w-9 h-9 rounded-full bg-ares-orange/20 border border-ares-orange/40 flex items-center justify-center font-heading font-bold text-ares-orange">
                            {user?.name?.[0]?.toUpperCase() || "A"}
                        </div>
                        <Button variant="ghost" size="icon" onClick={toggle} className="text-ares-muted hover:text-ares-orange hover:bg-ares-orange/10" data-testid="theme-toggle-btn" title="Toggle theme">
                            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={logout} className="text-ares-muted hover:text-destructive hover:bg-destructive/10" data-testid="logout-btn">
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Nav tabs */}
                <nav className="border-t border-ares-border/60">
                    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setActive(t.id)}
                                data-testid={t.testid}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                                    active === t.id ? "tab-active" : "border-transparent text-ares-muted hover:text-ares-bright"
                                }`}
                            >
                                <t.icon className="w-4 h-4" /> {t.label}
                                {isLocked(t.id) && <Lock className="w-3 h-3 text-ares-muted" />}
                            </button>
                        ))}
                    </div>
                </nav>

                {/* Trial countdown banner */}
                {trialDaysLeft !== null && (
                    <div className="border-t border-ares-amber/30 bg-ares-amber/10" data-testid="trial-countdown-banner">
                        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-2 flex items-center gap-3 flex-wrap">
                            <Clock className="w-4 h-4 text-ares-amber shrink-0" />
                            <p className="text-sm text-ares-bright">
                                <span className="font-semibold">{trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left</span> in your free trial.
                            </p>
                            <Button size="sm" onClick={() => setActive("plans")} className="ml-auto h-7 bg-ares-amber hover:bg-ares-amber/80 text-ares-void font-semibold" data-testid="trial-manage-btn">
                                <Zap className="w-3.5 h-3.5 mr-1.5" /> Manage plan
                            </Button>
                        </div>
                    </div>
                )}
            </header>

            <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
                {/* Metrics strip */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                    <MetricCard icon={FolderKanban} label="Active Projects" value={stats?.projects ?? "—"} accent="bg-ares-orange/15 text-ares-orange" testid="metric-projects" />
                    <MetricCard icon={GraduationCap} label="Stages Done" value={stats ? `${stats.stages_completed}/${stats.total_stages}` : "—"} accent="bg-ares-amber/15 text-ares-amber" testid="metric-stages" />
                    <MetricCard icon={Globe} label="Regions" value={stats?.regions_catalogued ?? "—"} accent="bg-ares-cyan/15 text-ares-cyan" testid="metric-regions" />
                    <MetricCard icon={Snowflake} label="High-Ice Zones" value={stats?.high_ice_regions ?? "—"} accent="bg-ares-cyan/15 text-ares-cyan" testid="metric-ice" />
                    <MetricCard icon={Layers} label="Data Layers" value="5" accent="bg-ares-emerald/15 text-ares-emerald" testid="metric-layers" />
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -12 }}
                        transition={{ duration: 0.25 }}
                    >
                        {active === "gis" && <GisMap />}
                        {active === "ground" && <GroundSciences />}
                        {active === "classifier" && (isLocked("classifier") ? <LockedModule title="AI Classifier" onUpgrade={() => setActive("plans")} /> : <AiClassifier onSaved={loadStats} />)}
                        {active === "workspace" && <ProjectWorkspace onChange={loadStats} />}
                        {active === "learning" && <LearningPath onChange={loadStats} />}
                        {active === "telemetry" && (isLocked("telemetry") ? <LockedModule title="Telemetry Hub" onUpgrade={() => setActive("plans")} /> : <TelemetryHub />)}
                        {active === "plans" && <Plans />}
                    </motion.div>
                </AnimatePresence>
            </main>
        </div>
    );
}
